import { useId, useRef, useState } from 'react';
import { Link, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Building2, Filter, Handshake, Info, Pencil, Plus, Trash2, Upload, X } from 'lucide-react';
import { Abas, PainelAba } from '../../componentes/abas';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { ConteudoAjuda } from '../../componentes/ajuda-tela';
import { Chatter } from '../../componentes/chatter';
import { Sensivel } from '../../componentes/sensivel';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { Entrada, linhasExtras, Rotulo, Selecao, useCamposPersonalizados, valorExtra } from '../../componentes/formulario';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { get, getTodos, mensagemDeErro, patch, post } from '../../nucleo/api';
import { data, dataHora, dinheiro, email, nomeParceiro, nomePessoa, numero, telefoneFormatado, textoLivre } from '../../nucleo/formato';
import { rotuloEtapaPipeline } from '../../nucleo/rotulos';
import { chaveDaTelaPeloCaminho } from '../../nucleo/chave-da-tela';
import { useSessao } from '../../nucleo/sessao';
import { AtalhoDeTarefas } from '../../nucleo/atalho-tarefas';
import { DialogoCriacao, useAbrirCriacao, useContextoDaCriacao, useCriacaoDaChave } from '../../nucleo/criacao-rapida';
import { useHistoricoDoParceiro } from './historico-do-parceiro';
import { ImportarCsv } from './ImportarCsv';
import { CartaoVerticaisRelacionamento, chaveVerticaisRelacionamento } from './VerticaisRelacionamento';
import {
  BarraSelecao,
  baixarCsv,
  BASES_LEGAIS,
  CabecalhoPagina,
  CAMINHO_CONTATOS,
  CAMINHO_EMPRESAS,
  caminhoDaNegociacaoCrm,
  ConfirmarContagem,
  CriarNegociacao,
  CriarNegociacaoEmLote,
  desvincularContato,
  emMassa,
  empresasDoContato,
  EXTRAS_FORA_DO_CRM,
  FiltroPeriodo,
  FiltrosSalvos,
  GavetaSobre,
  idsDoFiltro,
  invalidarVinculos,
  LinhaEditavel,
  Iniciais,
  registrosPorIds,
  textoDosResponsaveis,
  useAvisoDeReleitura,
  useAvisoEmMassa,
  usePodeNegociar,
  vincularContato,
  VincularEmpresa,
  type ParceiroCrm, registroInacessivel } from './crm-comum';
import { contarFiltros, paramsDosFiltros } from './crm-filtros';
import { FormContato, FormEmpresa } from './CrmFormularios';
import { useSelecaoCrm } from './crm-selecao';
import { SeloVerticais, useEmpresasDoCatalogo } from '../../componentes/verticais';
import { LinkVoltar } from '../../nucleo/voltar';

/**
 * CRM no novo desenho (lote 1, C-C): página Contatos do clone do RD sobre o cadastro único (pessoas, `papel=contato`, com a
 * empresa pelo vínculo de contato). Lista com filtros, seleção em massa (base legal, criar negociação, exportar, excluir),
 * "Sobre o contato" e a ficha (dados, empresa, negociações e a privacidade de dados).
 *
 * Ajustes de 27/10 (trilha cadastros): filtros do RD (nome, telefone, e-mail, período de criação e filtros salvos; C1); todos
 * os telefones e e-mails na lista (C22); a ficha com abas Dados do contato e Histórico, ações no cartão, telefones com tipo,
 * usuário do WhatsApp e campos personalizados (C4, C20, C21), e a empresa vinculável ali mesmo (C3).
 */
export function Contatos() {
  return (
    <Routes>
      <Route index element={<ListaContatos />} />
      <Route path=":id" element={<FichaContato />} />
    </Routes>
  );
}

type Filtros = Record<string, string>;
const limpos = (f: Filtros) => Object.fromEntries(Object.entries(f).filter(([, v]) => v !== '' && v !== undefined)) as Filtros;
const FILTROS_DO_PAINEL = ['vazio', 'nome', 'cargo', 'telefone', 'email', 'criado_preset', 'criado_desde', 'criado_ate', 'parent_id', 'base_legal'];
export const TELA_FILTROS_CONTATOS = 'cadastros.crm-contatos-filtros';

/** Ajustes de 27/10 (C22): o principal e os adicionais, cada um pelo formatador (modo discreto), separados por vírgula. */
export function todosOsMeios(principal: string | null, p: Pick<ParceiroCrm, 'meios'>, meio: 'telefone' | 'email', fmt: (v: string) => string, tipo?: { principal?: string | null; rotulo: (tipo: string) => string }) {
  // Lista de 02/11 (item 1): com `tipo`, cada telefone sai com o rótulo do tipo ("Celular: (11) 99999-9999"); o principal sem tipo gravado sai sem rótulo.
  return [{ v: principal, t: tipo?.principal ?? null }, ...(p.meios ?? []).filter((m) => m.meio === meio).map((m) => ({ v: m.valor as string | null, t: m.tipo as string | null }))]
    .filter((x): x is { v: string; t: string | null } => !!x.v)
    .map((x) => (tipo && x.t ? `${tipo.rotulo(x.t)}: ${fmt(x.v)}` : fmt(x.v)))
    .join(', ');
}

function ListaContatos() {
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const navegar = useNavigate();
  const podeNegociar = usePodeNegociar();
  const aviso = useAvisoEmMassa();
  const toast = useToast();
  const [f, setF] = useState<Filtros>({});
  const [painel, setPainel] = useState(false);
  const [criar, setCriar] = useState(false);
  // rcrm4 SPEC-M1: "Importar dados" das Configurações do CRM chega aqui com `?importar=1` e abre a importação (com a permissão).
  const [busca] = useSearchParams();
  // Rodada rlote-6 (SPEC-B1): o importador do CRM exige também `pipeline.criar` (os criados nascem lead).
  const [importar, setImportar] = useState(() => busca.get('importar') === '1' && s.pode('cadastros.importar') && s.pode('pipeline.criar'));
  const [sobre, setSobre] = useState<ParceiroCrm | null>(null);
  const [acao, setAcao] = useState<null | 'base_legal' | 'arquivar'>(null);
  const [valor, setValor] = useState('');
  const [negociar, setNegociar] = useState<ParceiroCrm[] | null>(null);
  const [marcandoTodos, setMarcandoTodos] = useState(false);
  const sel = useSelecaoCrm<ParceiroCrm>((p) => nomePessoa(p.nome));
  const params = { papel: 'contato', com_resumo: true, ...paramsDosFiltros(f) };
  const noPainel = contarFiltros(f, FILTROS_DO_PAINEL);
  const nCt = (n: number) => t('crm.n.contato', { count: n });

  const colunas: Coluna<ParceiroCrm>[] = [
    { chave: 'nome', rotulo: t('crm.campos.contato'), ordenavel: true, principal: true, render: (p) => <span className="font-medium text-azul">{nomePessoa(p.nome)}</span> },
    { chave: 'parent_nome', rotulo: t('crm.campos.empresa'), render: (p) => empresasDoContato(p).map((x) => nomePessoa(x.nome)).join(', ') },
    { chave: 'email', rotulo: t('crm.campos.email'), render: (p) => todosOsMeios(p.email, p, 'email', email) },
    { chave: 'telefone', rotulo: t('crm.campos.telefone'), mono: true, render: (p) => todosOsMeios(p.telefone, p, 'telefone', telefoneFormatado, { principal: p.telefone_tipo, rotulo: (x) => t(`crm.aj.tiposTelefone.${x === 'pessoal' ? 'outro' : x}`) }) },
    { chave: 'cargo', rotulo: t('crm.campos.cargo'), render: (p) => textoLivre(p.cargo, p.nome) },
    { chave: 'negociacoes', rotulo: t('crm.campos.negociacoes'), alinhar: 'direita', mono: true, render: (p) => numero(p.comercial?.total ?? 0) },
    {
      chave: 'sobre',
      rotulo: t('crm.contatos.sobre'),
      render: (p) => (
        <button
          type="button"
          data-sobre=""
          aria-label={`${t('crm.contatos.sobre')}: ${nomePessoa(p.nome)}`}
          title={t('crm.contatos.sobre')}
          onClick={(e) => {
            e.stopPropagation();
            setSobre(p);
          }}
          className="flex size-11 items-center justify-center rounded-lg text-tinta-3 hover:bg-sup-2 hover:text-tinta-1 md:size-8"
        >
          <Info className="size-4" aria-hidden />
        </button>
      ),
    },
    { chave: 'base_legal', rotulo: t('crm.campos.baseLegal'), oculta: true, render: (p) => t(`crm.baseLegal.${p.base_legal ?? 'nao_sabe'}`) },
    { chave: 'created_at', rotulo: t('crm.campos.criadoEm'), ordenavel: true, oculta: true, mono: true, render: (p) => data(p.created_at) },
  ];

  const confirmar = async (confirmacao: number) => {
    try {
      await aviso(await emMassa(sel.ids, acao!, acao === 'base_legal' ? valor : null, confirmacao));
      sel.limpar();
      setAcao(null);
    } catch (e) {
      toast.erro(mensagemDeErro(e));
    }
  };
  const exportar = async () => {
    try {
      const itens = await registrosPorIds<ParceiroCrm>(sel.ids);
      baixarCsv('contatos.csv', [
        [t('crm.campos.contato'), t('crm.campos.empresa'), t('crm.campos.email'), t('crm.campos.telefone'), t('crm.campos.cargo')],
        ...itens.map((p) => [p.nome, empresasDoContato(p).map((x) => x.nome).join(', '), todosOsMeios(p.email, p, 'email', String), todosOsMeios(p.telefone, p, 'telefone', String), p.cargo]),
      ]);
      toast.sucesso(t('crm.massa.exportado'));
    } catch (e) {
      toast.erro(mensagemDeErro(e));
    }
  };

  return (
    <div data-pagina-crm="contatos">
      <CabecalhoPagina titulo={t('crm.contatos.titulo')}>
        {/* C2: atalho de tarefas (ligação das trilhas, publicado pelo Pipeline). */}
        <AtalhoDeTarefas />
        {s.pode('cadastros.importar') && s.pode('pipeline.criar') && (
          <Botao icone={<Upload className="size-4" />} onClick={() => setImportar(true)}>
            {t('acoes.importar')}
          </Botao>
        )}
        {s.pode('cadastros.criar') && (
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setCriar(true)}>
            {t('crm.contatos.criar')}
          </Botao>
        )}
      </CabecalhoPagina>
      <BarraSelecao
        n={sel.n}
        aoLimpar={sel.limpar}
        todos={{
          rotulo: t('crm.contatos.selecionarTodos'),
          carregando: marcandoTodos,
          aoMarcar: async () => {
            setMarcandoTodos(true);
            try {
              sel.marcarTodos(await idsDoFiltro(params));
            } finally {
              setMarcandoTodos(false);
            }
          },
        }}
      >
        {s.pode('cadastros.editar') && (
          <Botao tamanho="sm" onClick={() => (setValor(''), setAcao('base_legal'))}>
            {t('crm.campos.baseLegal')}
          </Botao>
        )}
        {podeNegociar && (
          <Botao tamanho="sm" onClick={async () => setNegociar(await registrosPorIds<ParceiroCrm>(sel.ids))}>
            {t('crm.acoes.criarNegociacao')}
          </Botao>
        )}
        {/* Val5 (29/10): exportar tem ação própria (`cadastros.exportar`); arquivar em massa, `cadastros.arquivar` (a rota pede também `cadastros.editar`). */}
        {s.pode('cadastros.exportar') && (
          <Botao tamanho="sm" onClick={() => void exportar()}>
            {t('crm.acoes.exportar')}
          </Botao>
        )}
        {s.pode('cadastros.arquivar') && s.pode('cadastros.editar') && (
          <Botao tamanho="sm" variante="perigo" onClick={() => setAcao('arquivar')}>
            {t('crm.acoes.excluir')}
          </Botao>
        )}
      </BarraSelecao>
      <Tabela<ParceiroCrm>
        tela="cadastros.crm-contatos"
        caminho="/cadastros/parceiros"
        params={params}
        chaveConsulta={['crm', 'contatos']}
        filtros={f}
        aoAplicarFiltros={(x) => setF(Object.fromEntries(Object.entries(x).map(([k, v]) => [k, String(v)])))}
        buscaNaUrl
        filtrosExtra={
          <>
            {/* crm8 (2): lead (só no CRM) ou cliente (já ganhou). */}
            <div className="w-full sm:w-40">
              <Selecao
                aria-label={t('crm.fase.rotulo')}
                data-filtro-fase=""
                value={f.fase ?? ''}
                onChange={(ev) => setF((x) => ({ ...x, fase: ev.target.value }))}
                vazio={t('crm.fase.todas')}
                opcoes={(['lead', 'cliente'] as const).map((x) => ({ valor: x, rotulo: t(`crm.fase.${x}`) }))}
              />
            </div>
            <Botao icone={<Filter className="size-4" />} onClick={() => setPainel(true)} data-abrir-filtros="">
              {t('crm.filtros.botao', { n: noPainel })}
            </Botao>
          </>
        }
        selecao={sel.selecao}
        colunas={colunas}
        entidade="cadastros.parceiro"
        aoClicar={(p) => navegar(`${CAMINHO_CONTATOS}/${p.id}`)}
        vazio={<Vazio texto={t('crm.contatos.vazio')} />}
      />
      <PainelFiltros
        aberto={painel}
        aoFechar={() => setPainel(false)}
        atual={f}
        aoAplicar={(novo) => {
          setF((x) => ({ ...Object.fromEntries(Object.entries(x).filter(([k]) => !FILTROS_DO_PAINEL.includes(k))), ...novo }));
          setPainel(false);
        }}
      />
      {acao && (
        <ConfirmarContagem
          aberto
          aoFechar={() => setAcao(null)}
          titulo={acao === 'base_legal' ? t('crm.massa.baseLegalTitulo') : t('crm.massa.excluirTitulo')}
          texto={acao === 'base_legal' ? t('crm.massa.baseLegalTexto', { n: nCt(sel.n) }) : t('crm.contatos.excluirTexto', { n: nCt(sel.n) })}
          botao={acao === 'base_legal' ? t('crm.massa.baseLegalBotao') : t('crm.massa.excluirBotao')}
          perigoso={acao === 'arquivar'}
          n={sel.n}
          podeConfirmar={acao === 'arquivar' || !!valor}
          aoConfirmar={confirmar}
        >
          {acao === 'base_legal' && (
            <Selecao aria-label={t('crm.campos.baseLegal')} value={valor} onChange={(e) => setValor(e.target.value)} vazio={tn('comum.selecione')} opcoes={BASES_LEGAIS.map((b) => ({ valor: b, rotulo: t(`crm.baseLegal.${b}`) }))} />
          )}
        </ConfirmarContagem>
      )}
      {sobre && (
        <GavetaSobre
          aberto
          aoFechar={() => setSobre(null)}
          titulo={t('crm.contatos.sobre')}
          abrir={{ rotulo: t('crm.contatos.abrir'), para: `${CAMINHO_CONTATOS}/${sobre.id}` }}
          linhas={[
            [t('crm.campos.criadoEm'), data(sobre.created_at)],
            [t('crm.campos.empresa'), empresasDoContato(sobre).map((x) => nomePessoa(x.nome)).join(', ') || t('crm.campos.naoPreenchido')],
            [t('crm.campos.cargo'), textoLivre(sobre.cargo, sobre.nome) || t('crm.campos.naoPreenchido')],
            [t('crm.campos.telefone'), telefoneFormatado(sobre.telefone) || t('crm.campos.naoPreenchido')],
            [t('crm.campos.email'), email(sobre.email) || t('crm.campos.naoPreenchido')],
          ]}
        />
      )}
      {negociar && <CriarNegociacaoEmLote parceiros={negociar} aoFechar={() => setNegociar(null)} aoCriar={sel.limpar} />}
      <FormContato aberto={criar} aoFechar={() => setCriar(false)} aoSalvar={(id) => navegar(`${CAMINHO_CONTATOS}/${id}`)} criarOutra />
      {/* Rodada rlote-5 (SPEC-M1): pelo CRM, os criados nascem lead (fora da operação até o ganho). */}
      <ImportarCsv crm aberto={importar} aoFechar={() => setImportar(false)} />
    </div>
  );
}

/** O drawer "Filtros" do RD (C1): filtros salvos, campos vazios, nome, cargo, telefone, e-mail, data de criação, empresa e base legal. */
function PainelFiltros({ aberto, aoFechar, atual, aoAplicar }: { aberto: boolean; aoFechar: () => void; atual: Filtros; aoAplicar: (f: Filtros) => void }) {
  const { t } = useTranslation('cadastros');
  const id = useId();
  const [f, setF] = useState<Filtros>(atual);
  const [aberturaAnterior, setAberturaAnterior] = useState(aberto);
  if (aberto !== aberturaAnterior) {
    setAberturaAnterior(aberto);
    if (aberto) setF(atual);
  }
  const empresas = useQuery({ queryKey: ['crm', 'empresas-opcoes'], queryFn: () => getTodos<{ id: string; nome: string }>('/cadastros/parceiros', { papel: 'empresa' }), enabled: aberto });
  const mudar = (k: string, v: string) => setF((x) => ({ ...x, [k]: v }));
  const campo = (k: string) => ({ value: f[k] ?? '', onChange: (e: { target: { value: string } }) => mudar(k, e.target.value) });
  const doPainel = (x: Filtros) => limpos(Object.fromEntries(FILTROS_DO_PAINEL.map((k) => [k, x[k] ?? ''])));
  return (
    <Drawer
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={t('crm.filtros.titulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={() => aoAplicar({})}>
            {t('crm.filtros.limpar')}
          </Botao>
          <Botao variante="primario" data-aplicar-filtros="" onClick={() => aoAplicar(doPainel(f))}>
            {t('crm.filtros.aplicar')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <FiltrosSalvos tela={TELA_FILTROS_CONTATOS} filtros={doPainel(f)} aoAplicar={(x) => aoAplicar(doPainel(x))} />
        <Rotulo id={`${id}-vazio`} rotulo={t('crm.filtros.vazio')}>
          <Selecao id={`${id}-vazio`} {...campo('vazio')} vazio={t('crm.filtros.nenhum')} opcoes={(['email', 'telefone', 'cargo', 'empresa'] as const).map((v) => ({ valor: v, rotulo: t(`crm.filtros.vazioOpcoes.${v}`) }))} />
        </Rotulo>
        <Rotulo id={`${id}-nome`} rotulo={t('crm.aj.nomeDoContato')}>
          <Entrada id={`${id}-nome`} data-filtro-nome="" {...campo('nome')} />
        </Rotulo>
        <Rotulo id={`${id}-cargo`} rotulo={t('crm.campos.cargo')}>
          <Entrada id={`${id}-cargo`} {...campo('cargo')} />
        </Rotulo>
        <Rotulo id={`${id}-tel`} rotulo={t('crm.campos.telefone')}>
          <Entrada id={`${id}-tel`} inputMode="tel" data-filtro-telefone="" {...campo('telefone')} />
        </Rotulo>
        <Rotulo id={`${id}-email`} rotulo={t('crm.campos.email')}>
          <Entrada id={`${id}-email`} data-filtro-email="" {...campo('email')} />
        </Rotulo>
        <FiltroPeriodo campo="criado" rotulo={t('crm.campos.criadoEm')} f={f} mudar={mudar} />
        <Rotulo id={`${id}-emp`} rotulo={t('crm.campos.empresa')}>
          <Selecao id={`${id}-emp`} {...campo('parent_id')} vazio={t('crm.filtros.todos')} opcoes={(empresas.data?.itens ?? []).map((x) => ({ valor: x.id, rotulo: nomePessoa(x.nome) }))} />
        </Rotulo>
        <p className="kicker mt-2">{t('crm.filtros.processamento')}</p>
        <Rotulo id={`${id}-bl`} rotulo={t('crm.campos.baseLegal')}>
          <Selecao id={`${id}-bl`} {...campo('base_legal')} vazio={t('crm.filtros.todos')} opcoes={BASES_LEGAIS.map((b) => ({ valor: b, rotulo: t(`crm.baseLegal.${b}`) }))} />
        </Rotulo>
      </div>
    </Drawer>
  );
}

/** Teto do cartão de negociações da ficha; acima dele o cartão avisa o corte. */
const LIMITE_NEGOCIACOES = 100;

interface Negociacao {
  id: string;
  titulo: string;
  parceiro_nome: string | null;
  etapa_nome: string;
  valor_centavos: number;
  moeda: string;
  responsavel_nome: string | null;
  estado: 'aberta' | 'ganha' | 'perdida';
  pausada_em: string | null;
  empresas?: string[];
}

/**
 * Status da negociação como no RD (Vendida, Perdida, Pausada, Em andamento): rótulo e a cor da borda e do selo. Em andamento
 * e na primeira etapa do funil, "Nova" (crm4 L3); sem o dado da etapa, segue "Em andamento".
 */
export function statusDaNegociacao(n: Pick<Negociacao, 'estado' | 'pausada_em'> & { primeira_etapa?: boolean }): { chave: 'vendido' | 'perdido' | 'pausado' | 'nova' | 'andamento'; cor: 'verde' | 'perigo' | 'laranja' | 'azul' | 'ciano' } {
  if (n.estado === 'ganha') return { chave: 'vendido', cor: 'verde' };
  if (n.estado === 'perdida') return { chave: 'perdido', cor: 'perigo' };
  if (n.pausada_em) return { chave: 'pausado', cor: 'laranja' };
  if (n.primeira_etapa) return { chave: 'nova', cor: 'ciano' };
  return { chave: 'andamento', cor: 'azul' };
}
const BORDAS = { verde: 'border-l-verde', perigo: 'border-l-perigo', laranja: 'border-l-laranja', azul: 'border-l-azul', ciano: 'border-l-ciano' } as const;

/**
 * Rodada rrl-2 (SPEC-M1, item 4 da ficha 2026-11-03): a reunião marcada pela ficha do contato segue na ficha. O histórico recebe o
 * que outros módulos ligam ao contato (reuniões, tarefas), como o da empresa.
 */
function HistoricoContato({ c, nomes, criarTarefa }: { c: ParceiroCrm; nomes: (string | null | undefined)[]; criarTarefa: (() => void) | null }) {
  const { t } = useTranslation('cadastros');
  const { relacionados, cortado, nomes: nomesDoHistorico } = useHistoricoDoParceiro({ chave: ['crm', 'contato', c.id], parceiroId: c.id, nomes });
  return (
    <div className="flex flex-col gap-3" data-historico-contato="" data-relacionados={relacionados.length}>
      {cortado && (
        <p className="text-sm text-tinta-2" role="status" data-historico-cortado="">
          {cortado}
        </p>
      )}
      <Chatter variante="linha" criarTarefa={criarTarefa} marco={{ rotulo: t('crm.aj.contatoCriado'), data: c.created_at }} entidade="cadastros.parceiro" registroId={c.id} titulo={c.nome} nomes={nomesDoHistorico} relacionados={relacionados} />
    </div>
  );
}

function FichaContato() {
  const { id = '' } = useParams();
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const navegar = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const idAbas = useId();
  const [aba, setAba] = useState<'dados' | 'historico'>('dados');
  const raizFicha = useRef<HTMLDivElement>(null);
  const [editar, setEditar] = useState(false);
  const [excluir, setExcluir] = useState(false);
  const [vincular, setVincular] = useState(false);
  const [novaEmpresa, setNovaEmpresa] = useState(false);
  const [mudandoEmpresa, setMudandoEmpresa] = useState(false);
  const [ajuda, setAjuda] = useState(false);
  // crm4 (L2): o formulário completo de negociação (publicado pelo Pipeline), com o contato e a empresa dele já escolhidos.
  const criarNegociacao = useCriacaoDaChave('negociacao');
  // Lista de 02/11, item 6: o "Criar tarefa" do histórico abre o mesmo formulário do botão flutuante, com a empresa (ou o contato).
  const criacaoTarefa = useCriacaoDaChave('tarefa');
  const criacao = useAbrirCriacao();
  const [negociarCurto, setNegociarCurto] = useState(false);
  const podeNegociar = usePodeNegociar();
  const telaAjuda = chaveDaTelaPeloCaminho(useLocation().pathname);
  const extrasDefs = useCamposPersonalizados('cadastros.parceiro');
  const q = useQuery({ queryKey: ['crm', 'contato', id], queryFn: () => get<ParceiroCrm>(`/cadastros/parceiros/${id}`) });
  const veNegociacoes = s.instalado('pipeline') && s.pode('pipeline.ver');
  const catalogo = useEmpresasDoCatalogo();
  const negs = useQuery({ queryKey: ['crm', 'contato', id, 'negociacoes'], queryFn: () => get<{ itens: Negociacao[]; proximo_cursor?: string | null }>('/pipeline/oportunidades', { parceiro_id: id, incluir_contato: 'true', limite: LIMITE_NEGOCIACOES }), enabled: veNegociacoes });
  // Botão flutuante (C6): contato novo nasce na empresa; negociação nova, com a empresa e o contato.
  useContextoDaCriacao(q.data ? { contato: { id, nome: q.data.nome }, ...(q.data.parent_id && q.data.parent_nome && !q.data.parent_arquivada ? { empresa: { id: q.data.parent_id, nome: q.data.parent_nome } } : {}) } : {});
  useAvisoDeReleitura(q);
  // Rodada rcn-4 (ROB-M1): releitura que falha com dado em cache não desmonta a ficha, nem o formulário aberto nela.
  if (!q.data || registroInacessivel(q.error)) return q.isError ? <Erro erro={q.error} aoTentar={() => q.refetch()} /> : <Carregando />;
  const c = q.data;
  const podeEditar = s.pode('cadastros.editar');
  // Lista de 02/11 (item 2a): a rota recusa contato arquivado; o botão some junto.
  const podeVincular = podeEditar && !c.arquivado;
  const empresas = empresasDoContato(c);
  // Rlote-9 (SPEC-B3): negociação criada ou mudada na ficha atualiza também o cartão Verticais na hora (crm8 item 1).
  const recarregar = () => Promise.all([qc.invalidateQueries({ queryKey: ['crm', 'contato', id] }), qc.invalidateQueries({ queryKey: chaveVerticaisRelacionamento(id) })]);
  // Trocar a principal ou remover some com o botão clicado: o foco volta ao "Vincular empresa".
  const mudarEmpresa = async (fazer: () => Promise<unknown>, sucesso: string) => {
    setMudandoEmpresa(true);
    try {
      await fazer();
      await invalidarVinculos(qc);
      toast.sucesso(sucesso);
      // rl0211-7 (VIS-B1): o botão desta ficha; na tela dividida há outra ficha no mesmo documento.
      raizFicha.current?.querySelector<HTMLElement>('[data-abrir-vincular]')?.focus();
    } catch (e) {
      toast.erro(mensagemDeErro(e));
    } finally {
      setMudandoEmpresa(false);
    }
  };
  const abrirNegociacao = () => (criarNegociacao ? criacao.abrir(criarNegociacao) : setNegociarCurto(true));
  const naoPreenchido = <span className="text-tinta-3">{t('crm.campos.naoPreenchido')}</span>;
  const linha = (rotulo: string, valor: React.ReactNode, chave?: string) => (
    <p key={chave ?? rotulo} className="text-sm">
      <span className="text-tinta-3">{rotulo}: </span>
      <span className="font-medium text-tinta-1">{valor || naoPreenchido}</span>
    </p>
  );
  const usuarioWhats = typeof c.extras?.whatsapp_usuario === 'string' && c.extras.whatsapp_usuario ? c.extras.whatsapp_usuario : '';
  // Campos personalizados (C20), o usuário do WhatsApp à parte (com o "@", como no RD); todos pelo formatador do modo discreto.
  // O campo personalizado WhatsApp (telefone) não aparece aqui: virou tipo de telefone (lista de 02/11).
  const defsExtras = extrasDefs.filter((d) => d.nome !== 'extras.whatsapp_usuario' && !EXTRAS_FORA_DO_CRM.some((k) => d.nome === `extras.${k}`) && (!d.visivel || d.visivel({ tipo: 'pessoa', extras: c.extras })));
  const extras = linhasExtras(defsExtras, Object.fromEntries(Object.entries(c.extras ?? {}).filter(([k]) => k !== 'whatsapp_usuario' && !EXTRAS_FORA_DO_CRM.includes(k))), c.nome);
  const defWhats = extrasDefs.find((d) => d.nome === 'extras.whatsapp_usuario');
  // crm4 (L1): edição rápida do usuário do WhatsApp; grava sem o "@" (o prefixo é só visual) e mantém os outros campos personalizados.
  const editarUsuarioWhats = async (v: string) => {
    await patch(`/cadastros/parceiros/${id}`, { extras: { ...(c.extras ?? {}), whatsapp_usuario: v.replace(/^@+/, '') || null } });
    await recarregar();
    await qc.invalidateQueries({ queryKey: ['/cadastros/parceiros'] });
    toast.sucesso(tn('comum.salvo'));
  };
  const acaoIcone = (rotulo: string, icone: React.ReactNode, aoClicar: () => void, perigo = false) => (
    <button type="button" aria-label={rotulo} title={rotulo} onClick={aoClicar} className={`flex size-11 items-center justify-center rounded-lg hover:bg-sup-2 md:size-9 ${perigo ? 'text-perigo' : 'text-tinta-2'}`}>
      {icone}
    </button>
  );
  return (
    <div data-pagina-crm="contato" ref={raizFicha}>
      {q.isError && <Erro erro={q.error} aoTentar={() => q.refetch()} />}
      <CabecalhoPagina
        subtitulo={t('crm.contatos.titulo')}
        titulo={nomePessoa(c.nome)}
        voltar={
          <LinkVoltar para={CAMINHO_CONTATOS} aria-label={tn('comum.voltar')} title={tn('comum.voltar')} className="flex size-11 items-center justify-center rounded-lg text-tinta-2 hover:bg-sup-2 md:size-9">
            <ArrowLeft className="size-5" aria-hidden />
          </LinkVoltar>
        }
      >
        {/* crm8 (2) e rodada rlote-4 (SPEC-B1): a fase do contato, como no cabeçalho da empresa (vira cliente no ganho). */}
        <span data-selo-fase={c.situacao_comercial ?? 'cliente'}>
          <Selo cor={c.situacao_comercial === 'lead' ? 'azul' : 'ciano'}>{t(`situacoes.${c.situacao_comercial ?? 'cliente'}`)}</Selo>
        </span>
      </CabecalhoPagina>
      <Abas
        grupo={idAbas}
        rotulo={nomePessoa(c.nome)}
        ativa={aba}
        aoMudar={setAba}
        itens={[
          { chave: 'dados' as const, rotulo: t('crm.contatos.dados') },
          { chave: 'historico' as const, rotulo: t('crm.empresas.historico') },
        ]}
      />
      {aba === 'dados' ? (
        <PainelAba idBase={idAbas} chave="dados" className="mx-auto mt-4 flex max-w-2xl flex-col gap-4">
          <Cartao
            titulo={nomePessoa(c.nome)}
            acao={
              <span className="flex items-center">
                {podeNegociar && !veNegociacoes && (
                  <Botao tamanho="sm" variante="fantasma" icone={<Handshake className="size-4" />} onClick={abrirNegociacao} data-criar-negociacao-contato="">
                    {t('crm.acoes.criarNegociacao')}
                  </Botao>
                )}
                {podeEditar && acaoIcone(t('crm.contatos.editar'), <Pencil className="size-4" aria-hidden />, () => setEditar(true))}
                {s.pode('cadastros.arquivar') && acaoIcone(t('crm.acoes.excluir'), <Trash2 className="size-4" aria-hidden />, () => setExcluir(true), true)}
              </span>
            }
          >
            <div className="flex flex-col gap-1.5" data-dados-contato="">
              {linha(t('crm.campos.cargo'), textoLivre(c.cargo, c.nome))}
              {linha(t('crm.campos.sexo'), c.sexo ? <Sensivel>{t(`crm.sexo.${c.sexo}`)}</Sensivel> : '', 'sexo')}
              {/* Lista de 02/11 (item 1): o telefone principal mostra o rótulo do tipo (sem tipo gravado, "Telefone"). */}
              {linha(c.telefone_tipo && c.telefone ? t(`crm.aj.tiposTelefone.${c.telefone_tipo === 'pessoal' ? 'outro' : c.telefone_tipo}`) : t('crm.campos.telefone'), telefoneFormatado(c.telefone))}
              {(c.meios ?? [])
                .filter((m) => m.meio === 'telefone')
                .map((m) => linha(t(`crm.aj.tiposTelefone.${m.tipo === 'pessoal' ? 'outro' : m.tipo}`), telefoneFormatado(m.valor), m.id))}
              {linha(t('crm.campos.email'), email(c.email))}
              {(c.meios ?? []).filter((m) => m.meio === 'email').map((m) => linha(t('crm.campos.email'), email(m.valor), m.id))}
              {(usuarioWhats || (podeEditar && defWhats)) && (
                <LinhaEditavel
                  rotulo={defWhats?.rotulo ?? t('crm.aj.whatsapp')}
                  prefixo="@"
                  exibir={usuarioWhats && valorDoWhats(defWhats, usuarioWhats)}
                  valor={usuarioWhats}
                  pode={podeEditar}
                  aoSalvar={editarUsuarioWhats}
                  data-campo-rapido="whatsapp_usuario"
                />
              )}
              {extras.map((x) => linha(x.rotulo, x.valor, x.chave))}
              {linha(t('crm.campos.responsavel'), textoDosResponsaveis(c, t('crm.lbEmpresa.todos')))}
            </div>
          </Cartao>
          {/* Lista de 02/11 (item 2a): a principal e as adicionais; o botão fica onde e como o "Nova negociação". */}
          <Cartao
            titulo={t('crm.campos.empresa')}
            acao={
              podeVincular && (
                <Botao tamanho="sm" variante="fantasma" icone={<Building2 className="size-4" />} onClick={() => setVincular(true)} data-abrir-vincular="">
                  {t('crm.aj.vincularEmpresa')}
                </Botao>
              )
            }
          >
            {empresas.length ? (
              <ul className="flex flex-col gap-1" data-empresas-contato="">
                {empresas.map((x) => (
                  <li key={x.id} className="flex flex-wrap items-center gap-2 text-sm" data-empresa-contato={x.principal ? 'principal' : 'adicional'}>
                    <Link to={`${CAMINHO_EMPRESAS}/${x.id}`} title={nomePessoa(x.nome)} className="min-w-0 truncate font-semibold text-azul">
                      {nomePessoa(x.nome)}
                    </Link>
                    {x.principal && <span className="text-xs text-tinta-3">({t('crm.aj.principal')})</span>}
                    {!x.principal && podeVincular && (
                      <span className="ml-auto flex items-center">
                        <Botao tamanho="sm" variante="fantasma" disabled={mudandoEmpresa} aria-label={t('crm.aj.tornarPrincipalDe', { nome: nomePessoa(x.nome) })} onClick={() => void mudarEmpresa(() => vincularContato(c.id, x.id, true), t('crm.aj.empresaPrincipalTrocada'))} data-tornar-principal="">
                          {t('crm.aj.tornarPrincipal')}
                        </Botao>
                        <button
                          type="button"
                          disabled={mudandoEmpresa}
                          aria-label={t('crm.aj.removerMeio', { campo: nomePessoa(x.nome) })}
                          title={t('crm.aj.removerMeio', { campo: nomePessoa(x.nome) })}
                          onClick={() => void mudarEmpresa(() => desvincularContato(c.id, x.id), t('crm.aj.empresaRemovida'))}
                          data-remover-empresa=""
                          className="flex size-11 items-center justify-center rounded-lg text-tinta-3 hover:bg-sup-2 disabled:opacity-60 md:size-9"
                        >
                          <X className="size-4" aria-hidden />
                        </button>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-tinta-3" data-sem-empresa="">
                {t('crm.aj.semEmpresa')}
              </p>
            )}
          </Cartao>
          {/* crm8 (1): as verticais vêm das negociações (e dos contratos); aqui só leitura. */}
          <CartaoVerticaisRelacionamento parceiroId={c.id} />
          {veNegociacoes && (
            <Cartao
              titulo={t('crm.campos.negociacoes')}
              acao={
                // crm6 item 10: a negociação nova nasce na seção de negociações do contato, já com ele (e a empresa dele).
                podeNegociar && (
                  <Botao tamanho="sm" variante="fantasma" icone={<Handshake className="size-4" />} onClick={abrirNegociacao} data-criar-negociacao-contato="" data-nova-negociacao-contato="">
                    {t('crm.acoes.novaNegociacao')}
                  </Botao>
                )
              }
            >
              {!negs.data ? (
                negs.isError ? (
                  <Erro erro={negs.error} aoTentar={() => negs.refetch()} />
                ) : (
                  <Carregando />
                )
              ) : negs.data.itens.length ? (
                <>
                {negs.isError && <Erro erro={negs.error} aoTentar={() => negs.refetch()} />}
                <ul className="flex flex-col gap-2" data-negociacoes-contato="">
                  {negs.data.itens.map((n) => {
                    const st = statusDaNegociacao(n);
                    return (
                      <li key={n.id} className={`flex flex-wrap items-center gap-3 rounded-lg border-l-4 bg-sup-2 px-3 py-2 text-sm ${BORDAS[st.cor]}`}>
                        <Iniciais nome={n.responsavel_nome} />
                        <span className="min-w-0 flex-1">
                          <Link to={caminhoDaNegociacaoCrm(n.id)} className="font-semibold break-words text-azul">
                            {textoLivre(n.titulo, c.nome, c.parent_nome, ...empresas.map((x) => x.nome), n.parceiro_nome)}
                          </Link>
                          <span className="block text-xs text-tinta-3">
                            {[nomeParceiro(n.parceiro_nome), rotuloEtapaPipeline(n.etapa_nome), t(`crm.aj.status.${st.chave}`)].filter(Boolean).join(' · ')}
                          </span>
                        </span>
                        {/* Lista de 02/11, item 2b: a vertical de cada negociação. */}
                        <SeloVerticais empresas={n.empresas ?? []} catalogo={catalogo.data?.empresas ?? []} />
                        <span className="numero">{dinheiro(n.valor_centavos, n.moeda)}</span>
                      </li>
                    );
                  })}
                </ul>
                {negs.data.proximo_cursor && (
                  <p className="mt-2 text-xs text-tinta-3" role="status" data-negociacoes-cortadas="">
                    {t('crm.contatos.negociacoesCortadas', { limite: LIMITE_NEGOCIACOES })}
                  </p>
                )}
                </>
              ) : (
                <p className="text-sm text-tinta-3">{t('crm.empresas.semNegociacoes')}</p>
              )}
            </Cartao>
          )}
          <Cartao
            titulo={t('crm.contatos.privacidade')}
            acao={
              <button type="button" onClick={() => setAjuda(true)} data-saiba-mais-ajuda="" className="inline-flex min-h-11 items-center gap-1 text-xs text-azul underline-offset-2 hover:underline md:min-h-9">
                <Info className="size-3.5" aria-hidden />
                {t('crm.aj.saibaMaisAjuda')}
              </button>
            }
          >
            {linha(t('crm.campos.baseLegal'), t(`crm.baseLegal.${c.base_legal ?? 'nao_sabe'}`))}
          </Cartao>
          <p className="text-xs text-tinta-3">
            {t('crm.campos.criadoEm')}: {dataHora(c.created_at)}
          </p>
        </PainelAba>
      ) : (
        <PainelAba idBase={idAbas} chave="historico" className="mx-auto mt-4 max-w-3xl">
          <HistoricoContato c={c} nomes={[c.nome, c.parent_nome, ...empresas.map((x) => x.nome)]} criarTarefa={criacaoTarefa ? () => criacao.abrir(criacaoTarefa) : null} />
        </PainelAba>
      )}
      <FormContato aberto={editar} aoFechar={() => setEditar(false)} atual={c} aoSalvar={() => void recarregar()} />
      {/* L11 (C21): o "Saiba mais na Central de Ajuda" abre a ajuda desta tela (a mesma do painel Malgo IA). */}
      <DialogoCriacao
        aberta={criacao.aberta}
        aoFechar={criacao.fechar}
        contexto={{ contato: { id: c.id, nome: c.nome }, ...(c.parent_id && c.parent_nome && !c.parent_arquivada ? { empresa: { id: c.parent_id, nome: c.parent_nome } } : {}) }}
        aoCriar={() => void qc.invalidateQueries({ queryKey: ['crm', 'contato', id] })}
      />
      {negociarCurto && <CriarNegociacao aberto parceiros={[{ id: c.id, nome: c.nome }]} aoFechar={() => setNegociarCurto(false)} aoCriar={() => void recarregar()} />}
      <Drawer aberto={ajuda} aoFechar={() => setAjuda(false)} titulo={tn('ajudaTela.titulo')}>
        <ConteudoAjuda chave={telaAjuda.chave} caminho={telaAjuda.slug && telaAjuda.rotulo ? tn(`${telaAjuda.slug}:${telaAjuda.rotulo}`) : tn('menus.inicio')} aoNavegar={() => setAjuda(false)} />
      </Drawer>
      <VincularEmpresa
        aberto={vincular}
        aoFechar={() => setVincular(false)}
        contatoId={c.id}
        aoCadastrarNova={
          s.pode('cadastros.criar')
            ? () => {
                setVincular(false);
                setNovaEmpresa(true);
              }
            : undefined
        }
      />
      <FormEmpresa
        aberto={novaEmpresa}
        aoFechar={() => setNovaEmpresa(false)}
        aoSalvar={async (empresaId) => {
          try {
            await vincularContato(c.id, empresaId);
            toast.sucesso(t('crm.aj.empresaVinculada'));
          } catch (e) {
            toast.erro(mensagemDeErro(e));
          }
          await invalidarVinculos(qc);
          await recarregar();
        }}
      />
      <Confirmar
        aberto={excluir}
        aoFechar={() => setExcluir(false)}
        perigoso
        titulo={t('confirmar.arquivar')}
        texto={t('confirmar.arquivarTexto')}
        aoConfirmar={async () => {
          try {
            await post(`/cadastros/parceiros/${c.id}/arquivar`, {});
            await qc.invalidateQueries({ queryKey: ['/cadastros/parceiros'] });
            navegar(CAMINHO_CONTATOS);
          } catch (e) {
            toast.erro(mensagemDeErro(e));
            setExcluir(false);
          }
        }}
      />
    </div>
  );
}

/** O usuário do WhatsApp pela regra do campo personalizado (sensível: no modo discreto vira o marcador). */
function valorDoWhats(def: Parameters<typeof valorExtra>[0], v: string) {
  return valorExtra(def ? { ...def, nome: def.nome } : { tipo: 'texto', nome: 'extras.whatsapp_usuario', sensivelNaDefinicao: true }, v);
}
